import { useCallback, useEffect, useState } from 'react';

import type { NTagsModal } from '@app/components/TagsModal/TagsModal.types';
import type { NTags } from '@app/types/Tags';

export const useTagsModal = ({
  tags,
  setIsTagsModalOpen,
  updateTags,
  selectedTagsIds,
  setSelectedTagsIds,
}: NTagsModal.UseTagsModalProps) => {
  const [searchedTags, setSearchedTags] = useState<NTags.Tag[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedInitialTagsIds, setInitialSelectedTagsIds] =
    useState(selectedTagsIds);

  useEffect(() => {
    if (tags) {
      setSearchedTags(tags);
    }
  }, [tags]);

  const handleTagsSearch = useCallback(
    (value: string) => {
      setSearchedTags(
        tags.filter((tag) =>
          tag.Name?.toLocaleLowerCase().includes(value.toLocaleLowerCase())
        )
      );
    },
    [tags]
  );

  const handleCloseModal = useCallback(() => {
    setIsTagsModalOpen(false);
    setSearchedTags(tags);
    setSearchInputValue('');
    setSelectedTagsIds(selectedInitialTagsIds);
  }, [selectedInitialTagsIds, setIsTagsModalOpen, setSelectedTagsIds, tags]);

  const handleOk = useCallback(async () => {
    await updateTags();

    setInitialSelectedTagsIds(selectedTagsIds);

    handleCloseModal();
  }, [
    handleCloseModal,
    selectedTagsIds,
    setInitialSelectedTagsIds,
    updateTags,
  ]);

  const handleAfterOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setInitialSelectedTagsIds(selectedTagsIds);
      }
    },
    [selectedTagsIds]
  );

  return {
    handleTagsSearch,
    searchedTags,
    handleCloseModal,
    searchInputValue,
    setSearchInputValue,
    handleOk,
    selectedInitialTagsIds,
    setInitialSelectedTagsIds,
    handleAfterOpenChange,
  };
};
